import { Inject, Injectable } from '@angular/core';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { map, Observable, of, take } from 'rxjs';
import { AUTOMATION_CONTEXT_INJECTION_TOKEN, AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { switchMap } from 'rxjs/operators';
import { Assistant, AssistantApiService } from '@vendasta/ai-assistants';
import { Context } from '@vendasta/automata';
import { NamespaceInterface } from '@vendasta/ai-assistants/lib/_internal/interfaces/namespace.interface';

@Injectable({ providedIn: 'root' })
export class EmployeeSelectorService implements AutomationReplacementChipsDataService<Assistant> {
  supportsSearching = false;

  constructor(
    private readonly assistantApiService: AssistantApiService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) readonly namespace$: Observable<string>,
    @Inject(AUTOMATION_CONTEXT_INJECTION_TOKEN) readonly automationContext: Context,
  ) {}

  doLookup(_searchTerm: string, cursor: string, pageSize: number): Observable<PagedResponse<Assistant>> {
    return this.namespace$.pipe(
      map((namespace) => this.buildAssistantNamespace(namespace)),
      switchMap((namespace) =>
        this.assistantApiService.listAssistant({
          filters: { namespace: namespace },
          pagingOptions: {
            cursor: cursor,
            pageSize: pageSize,
          },
        }),
      ),
      take(1),
      map((res) => {
        const results = res?.assistants;
        const nextCursor = res?.metadata?.nextCursor;
        const hasMore = res?.metadata?.hasMore;
        return { results, nextCursor, hasMore };
      }),
    );
  }

  convertDataToDisplay(a: Assistant): Observable<string> {
    return of(a.name);
  }

  convertDataToFormValue(a: Assistant): any {
    return a.id;
  }

  getMulti(x: string[]): Observable<Assistant[]> {
    return this.namespace$.pipe(
      take(1),
      map((namespace) =>
        (x || []).map((id) => ({
          id: id,
          namespace: this.buildAssistantNamespace(namespace),
        })),
      ),
      switchMap((keys) =>
        this.assistantApiService.getMultiAssistant({
          assistantKeys: keys,
          options: { skipGoalsHydration: true },
        }),
      ),
      map((resp) => resp?.assistants),
    );
  }

  get(id: string): Observable<Assistant> {
    return this.getMulti([id]).pipe(
      map((assistants) => {
        if (assistants && assistants.length > 0) {
          return assistants[0];
        } else {
          throw new Error(`No assistant found with id: ${id}`);
        }
      }),
    );
  }

  buildAssistantNamespace(namespace: string): NamespaceInterface {
    if (this.automationContext == Context.AUTOMATION_CONTEXT_SMB) {
      return { accountGroupNamespace: { accountGroupId: namespace } };
    }
    return { partnerNamespace: { partnerId: namespace } };
  }
}
